import {
  DEFAULT_COL_WIDTH,
  DEFAULT_ROW_HEIGHT,
  MIN_COL_WIDTH,
  MIN_ROW_HEIGHT,
  type TableConfig,
} from './table-types';

/**
 * A table's own size, from its columns and rows rather than from its box.
 *
 * The element box (`req.width` / `req.height`) is what the canvas positions and
 * is often unset — a table authored in flow has never been given one. The sum of
 * the parts is the size that always exists, so it is the baseline a scale is
 * measured against when the box cannot supply one.
 */
export function tableNaturalSize(config: TableConfig): {width: number; height: number} {
  const width = config.columns.reduce((sum, c) => sum + (c.width ?? DEFAULT_COL_WIDTH), 0);
  const height = config.rows.reduce((sum, r) => sum + (r.height ?? DEFAULT_ROW_HEIGHT), 0);
  return {width, height};
}

/**
 * The same table at a new outer size, with its internal proportions preserved.
 *
 * **This is what makes a corner handle resize the TABLE and not just its box.**
 * `useResize` already gives every absolute element four corner handles that move
 * the origin correctly, and `handleElementResize` already stores the new box —
 * but a table's columns and rows carry their own widths and heights, so the box
 * grew and the grid inside it did not: the author dragged a corner and got the
 * same table with space around it. Scaling the parts by the same factor as the
 * whole is the missing half, and it belongs here rather than in the drag so the
 * arithmetic is testable without a mouse.
 *
 * Every column keeps its share of the width and every row its share of the
 * height, which is what "preserve the structure" means: a 2:1 pair of columns is
 * still 2:1 afterwards. Rounding is per-part and deliberately not corrected
 * against the total — a pixel of drift beats a table whose last column is a
 * pixel wider than its neighbours for no reason a reader could name.
 *
 * **Floors are per part, and they bound the whole by construction.** A column
 * cannot go under `MIN_COL_WIDTH`, so a table cannot be dragged narrower than
 * its column count times that; a row's floor is `MIN_ROW_HEIGHT`, which is 1,
 * because the browser already refuses to paint a row shorter than its content
 * (a `<tr>` height is a minimum) — that is what lets a table be dragged as
 * compact as Canva's while nothing inside it is ever clipped.
 *
 * An axis with no usable baseline is left alone rather than guessed at: scaling
 * against zero is a division by zero, and scaling against a size nobody set
 * would move parts the author never sized.
 */
export function scaleTableConfig(
  config: TableConfig,
  box: {fromWidth?: number; toWidth?: number; fromHeight?: number; toHeight?: number},
): TableConfig {
  const {fromWidth, toWidth, fromHeight, toHeight} = box;
  const scaleX = usableScale(fromWidth, toWidth);
  const scaleY = usableScale(fromHeight, toHeight);
  if (scaleX === null && scaleY === null) return config;

  const columns =
    scaleX === null
      ? config.columns
      : config.columns.map((col) => ({
          ...col,
          width: Math.max(
            col.minWidth ?? MIN_COL_WIDTH,
            Math.round((col.width ?? DEFAULT_COL_WIDTH) * scaleX),
          ),
        }));
  const rows =
    scaleY === null
      ? config.rows
      : config.rows.map((row) => ({
          ...row,
          height: Math.max(MIN_ROW_HEIGHT, Math.round((row.height ?? DEFAULT_ROW_HEIGHT) * scaleY)),
        }));

  return {
    ...config,
    columns,
    rows,
    // Kept in step with the columns it is meant to describe. Left alone when the
    // width did not change, so a purely vertical drag cannot pin a width the
    // table did not have before.
    tableWidth:
      scaleX === null
        ? config.tableWidth
        : columns.reduce((sum, c) => sum + (c.width ?? DEFAULT_COL_WIDTH), 0),
  };
}

/** The factor for one axis, or `null` when there is nothing honest to scale by. */
function usableScale(from: number | undefined, to: number | undefined): number | null {
  if (!from || !to || from <= 0 || to <= 0) return null;
  if (from === to) return null;
  return to / from;
}
